import { getModel } from '../model/registry'
import { useState, useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { APP_REGISTRY } from '../model/appRegistry.js'
import useUserPermissions from '../hooks/useUserPermissions'

import { Line } from '../components/generic/Layout'

// Importing sub-Components
import Sidebar from '../components/sidebar/Sidebar'
import Header from '../components/header/Header'
import Loader from '../components/generic/Loader'
import Reload from '../components/header/Reload'
import PreviewBanner from '../components/header/PreviewBanner'

//Import Syling Components
import { Container } from '../components/generic/Layout'

import useTranslation from '../hooks/useTranslation'
import { useSnapshot } from 'valtio'
import { useQueryClient } from '@tanstack/react-query'

import useProfile from '../hooks/useProfile'
import useAuth from '../hooks/useAuth'
import useApi from '../hooks/useApi'
import usePwaUpdate from '../hooks/usePwaUpdate'
import useThemeColor from '../hooks/useThemeColor'
import MobileNavBar from '../components/mobileNav/MobileNavBar'
import { DeadSpace } from '../components/mobileNav/MobileNavBar'
import useSSE from '../events/sse/useSSE'
import useGlobalShortcuts from '../hooks/useGlobalShortcuts'
import FastNavModal from '../components/fastNav/FastNavModal'

// Map from URL path segment (e.g. 'sales', 'purchases') to app entry
const pathSegmentToApp = Object.entries(APP_REGISTRY).reduce((acc, [appId, app]) => {
  if (app.url) {
    const segment = app.url.replace(/^\//, '').split('/')[0]
    acc[segment] = { appId, ...app }
  }
  return acc
}, {})

const PageContent = styled.div`
  min-width: 0;
//  padding-left: 0.5rem;
  color: ${({ theme }) => theme.font.color};
  background: ${({ theme }) => theme.area.background};
  flex: 1 0 0%;
  `

const AppArea = styled(Line)`
    min-height: 100vh;
`

const TopNotification = styled.div`
  background: ${({ theme }) => theme.btn.yellow};
  padding-block: 0.5rem;
`

const TopSafeArea = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: max(env(safe-area-inset-top), 1px);
  z-index: 9999;
  pointer-events: none;
  background: transparent;
`

const Layout = () => {
  //console.log('Rendered: Layout')

  const [isLoading, setIsLoading] = useState(true)
  const [currentApp, setCurrentApp] = useState('')
  const [fastNavOpen, setFastNavOpen] = useState(false)
  const { tState, loadTrans } = useTranslation()
  const { pState } = useProfile()

  const snap = useSnapshot(tState)
  const { initializeProfile } = useProfile()
  const profileSnap = useSnapshot(pState)

  const queryClient = useQueryClient()
  const { getMe } = useApi()

  // Initialize PWA update detection
  usePwaUpdate()

  // Sync Android theme-color meta tag with current theme
  useThemeColor()

  // Initialize SSE connection for server-to-client real-time events
  const { SSEToastComponent } = useSSE()

  // Global keyboard shortcuts (CTRL+K / CMD+K → app switcher)
  useGlobalShortcuts()

  // Ctrl+Shift+K → fast navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'K' || e.key === 'k')) {
        e.preventDefault()
        setFastNavOpen(open => !open)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  //Setting up a few things
  const { Auth } = useAuth()
  const navigate = useNavigate()
  const { isOwner, apps, isLoading: permissionsLoading } = useUserPermissions()
  let location = useLocation()
  let removeSidebar = false

  //Remove Sidebar if slug is "org" to hide navigation
  if (location.pathname === '/org') { removeSidebar = true }

  const loadTranslations = async () => {
    await loadTrans()
  }

  // Set loading to false when translations are ready
  useEffect(() => {
    if (snap.ready && isLoading) {
      setIsLoading(false)
    }
  }, [snap.ready])

  const loadSettings = () => {
    // Extract the first part of the path (e.g., 'sales' from 'sales/invoice')
    const path = location.pathname.replace(/^\//, "").split('/')[0];

    const settingKey = `${path}Settings`
    if (getModel('transferSettingsData')[settingKey]) {
      queryClient.prefetchQuery({
        queryKey: ['adminSettings'],
        queryFn: () => getMe('adminSettings'),
        staleTime: Infinity
      })
      queryClient.prefetchQuery({
        queryKey: [settingKey],
        queryFn: () => getMe(settingKey),
        staleTime: Infinity
      })
    }
  }

  //This initializes the profile on first load of the Layout
  useEffect(
    () => {
      initializeProfile()
    }, [Auth.orgId])

  // Update currentAppSection when pathname changes
  useEffect(() => {
    const newSection = location.pathname.replace(/^\//, "").split('/')[0];
    if (newSection !== currentApp) {
      setCurrentApp(newSection)
    }
  }, [location.pathname])

  // Load settings only when currentAppSection changes
  useEffect(
    () => {
      if (currentApp) {  // Only load if we have a section
        loadSettings()
      }
    }, [currentApp, Auth.orgId])

  // Reload the translations when language changes.
  useEffect(() => {
    loadTranslations()
  }, [snap.currentLang])

  // Guard: redirect to home if user navigates to an app they don't have access to
  useEffect(() => {
    if (permissionsLoading) return
    const segment = location.pathname.replace(/^\//, '').split('/')[0]
    if (!segment) return

    const appEntry = pathSegmentToApp[segment]
    if (!appEntry) return  // not a known app path, let the router handle it

    if (appEntry.ownerOnly && !isOwner) {
      navigate('/', { replace: true })
      return
    }

    if (!isOwner && !apps.includes(appEntry.appId)) {
      navigate('/', { replace: true })
    }
  }, [location.pathname, permissionsLoading, isOwner, apps])

  if (isLoading) {
    return (<Loader />)
  } else
    return (
      <Container id='Layout-Container'>
        <TopSafeArea />
        <AppArea id='AppArea'>
          {removeSidebar ? <></> :
            <Sidebar />}
          <PageContent id='Page-Content'>
            {profileSnap.newVersion && <TopNotification><Reload /></TopNotification>}
            {/* The owner's preview session, app-wide (30.03.08 §7) */}
            <PreviewBanner />
            {removeSidebar ? <></> :
              <Header />
            }
            <main>
              <Outlet key={`${location.pathname}/${snap.currentLang}`} />
            </main>
            <MobileNavBar />
            <DeadSpace />
          </PageContent>
        </AppArea>
        {SSEToastComponent}
        {fastNavOpen && <FastNavModal onClose={() => setFastNavOpen(false)} />}
      </Container>
    )
}

export default Layout
